import React, {useEffect, useRef, useState} from "react";
import { StyleSheet, View } from "react-native-web";
import { $black } from "../../common/constants/style";
import { vw } from "../../utils/tool/vw";
import { SingleDishVideo } from "../../common/component/HomeScreen/FullDishVideo/SingleDishVideo";
import { Ingredients } from "../../common/component/HomeScreen/Ingredients/Ingredients";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../utils/hook/redux-hooks";
import { fetchDishById, fetchMenu } from "../../store/redux/menuSlice";
import {DishType} from "../../common/types/ResponseType";
import {NutritionValue} from "../../common/component/HomeScreen/NutritionValue/NutritionValue";
import {FunFactComponent} from "../../common/component/HomeScreen/FunFact/FunFactCard";
import Analytics from "../../analytics/Analytics";
import {RootState} from "../../store/store";
import { useIsDesktop } from "../../utils/hook/useIsDesktop";
import { useCart } from "../../utils/hook/useCart";
import { useLocalizedContent } from "../../utils/hook/useLocalizedContent";
import { useMenuLocaleInit } from "../../utils/hook/useMenuLocaleInit";
import { useMenuLanguage } from "../../utils/hook/useMenuLanguage";
import { LanguageSwitcher } from "../../common/ui-kit/headers/LanguageSwitcher";
import pageStyles from "./SingleDishPage.module.scss";

export const SingleDishPage = () => {
  const { id: menuId, dishId } = useParams();
  const dispatch = useAppDispatch();
  const { menu, activeCategory } = useAppSelector((state: RootState) => state.menu);
  const { selectedDish } = useLocalizedContent();
  useMenuLocaleInit();
  const { locale, locales, showLanguageSwitcher, changeLocale } = useMenuLanguage();
  const isDesktop = useIsDesktop();
  const { attachMenu } = useCart();

  // Ensure menu is loaded when opening /menu/:id/dish/:dishId (e.g. direct link or refresh)
  useEffect(() => {
    if (menuId && (!menu || menu.id !== Number(menuId))) {
      dispatch(fetchMenu({ id: Number(menuId) }));
    }
  }, [menuId, menu, dispatch]);

  // A deep link into a dish is a valid entry point, so the cart binds to
  // this menu here too, not only on the menu page.
  useEffect(() => {
    if (menuId) attachMenu(Number(menuId));
  }, [menuId, attachMenu]);

  useEffect(() => {
    const category: string = activeCategory || "";
    const menu_id: string = menu?.id.toString() || "";
    Analytics.logDishVisit(selectedDish?.name ?? '', category, menu_id);
  }, [dishId, selectedDish]);

  useEffect(() => {
    const element = document.getElementById("DishPage");
    if (element !== null) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (dishId) {
      dispatch(fetchDishById({ dishId: Number(dishId) }));
    }
  }, [dishId, dispatch]);

  // Top choice badge: only when menu has it enabled; icon from menu settings (default or custom URL)
  const showTopChoiceBadge = !!(menu?.topChoiceEnabled && selectedDish?.isTopChoice);
  const topChoiceBadgeUrl =
    showTopChoiceBadge && menu?.topChoiceIconType === "custom" && menu?.topChoiceCustomIcon?.url
      ? menu.topChoiceCustomIcon.url
      : null;

  // Recommended badge: only when menu has it enabled; icon from menu settings (default or custom URL)
  const showRecommendedBadge = !!(menu?.recommendedEnabled && selectedDish?.isRecommended);
  const recommendedBadgeUrl =
    showRecommendedBadge && menu?.recommendedIconType === "custom" && menu?.recommendedCustomIcon?.url
      ? menu.recommendedCustomIcon.url
      : null;

  const languageSwitcher = showLanguageSwitcher ? (
    <LanguageSwitcher
      locale={locale}
      locales={locales}
      onChange={changeLocale}
    />
  ) : null;

  const dishVideo = (
    <div className={pageStyles.videoStage}>
      {languageSwitcher && (
        <div className={pageStyles.languageSlot}>{languageSwitcher}</div>
      )}
      <SingleDishVideo
        selectedDish={selectedDish}
        showTopChoiceBadge={showTopChoiceBadge}
        topChoiceBadgeUrl={topChoiceBadgeUrl ?? undefined}
        showRecommendedBadge={showRecommendedBadge}
        recommendedBadgeUrl={recommendedBadgeUrl ?? undefined}
      />
    </div>
  );
  const ingredients = <Ingredients selectedDish={selectedDish as DishType} />;
  const nutritionValue = <NutritionValue />;
  const funFact = selectedDish?.funFact ? <FunFactComponent /> : null;

  if (isDesktop) {
    return (
      <div id="DishPage" className={pageStyles.twoColumn}>
        <div className={pageStyles.videoColumn}>{dishVideo}</div>
        <div className={pageStyles.detailsColumn}>
          {ingredients}
          {nutritionValue}
          {funFact}
        </div>
      </div>
    );
  }

  return (
    <View id="DishPage" style={styles.wrapper}>
      <View style={{ display: "flex", flexDirection: "column", gap: vw(4) }}>
        {dishVideo}
        {ingredients}
        {nutritionValue}
        {funFact}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: $black,
  },
});
